import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { useNFTStore, useUIStore } from '../../stores';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { Toggle } from '../common/Toggle';
import { ImagePreviewModal } from '../common/ImagePreviewModal';
import { CropCanvas, isFillLayerByDefault } from './CropCanvas';
import { useCropInteraction } from '../../hooks';
import { preloadLayerImages, getDecodedImageFromCache } from '../../hooks/useLayerImage';
import { downloadImage } from '../../utils';

const CANVAS_SIZE_DESKTOP = 400;
const CANVAS_SIZE_MOBILE = 300; // 25% smaller for mobile
const EXPORT_SIZE = 1024;
const MOBILE_BREAKPOINT = 640; // sm breakpoint

export type CropModalMode = 'export' | 'sendToChat';

interface CropModalProps {
  /** 'export' for download-only, 'sendToChat' for Telegram-aware behavior */
  mode?: CropModalMode;
}

export function CropModal({ mode = 'sendToChat' }: CropModalProps) {
  const { currentNFT } = useNFTStore();
  const { activeModal, closeModal } = useUIStore();
  const [isExporting, setIsExporting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  // Sticker mode: removes background and frame, saves as transparent
  const [stickerMode, setStickerMode] = useState(false);
  // Circle mask always shown for PFP preview (hidden in sticker mode)
  const showCircleMask = !stickerMode;
  // Fixed layer IDs can be user-toggled in future UI enhancement
  const [fixedLayerIds] = useState<Set<string>>(new Set());
  // Preview modal state for Telegram fallback
  const [previewImage, setPreviewImage] = useState<{ url: string; filename: string } | null>(null);
  // Success notification state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Responsive canvas size for mobile
  const [canvasSize, setCanvasSize] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
      ? CANVAS_SIZE_MOBILE
      : CANVAS_SIZE_DESKTOP
  );

  // Update canvas size on window resize
  useEffect(() => {
    const handleResize = () => {
      setCanvasSize(window.innerWidth < MOBILE_BREAKPOINT ? CANVAS_SIZE_MOBILE : CANVAS_SIZE_DESKTOP);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const { offset, scale, minScale, setScale, setImageBounds, reset, isDragging } = useCropInteraction(
    containerRef,
    {
      maxScale: 3,
      initialScale: 1,
      canvasSize: canvasSize,
      verticalOnly: true, // Only allow vertical dragging for PFP crop
    }
  );

  const isOpen = activeModal === 'crop';
  const imageUrl = currentNFT?.imageUrl || '';
  const layerAssets = currentNFT?.layerAssets || [];
  const useLayerMode = layerAssets.length > 0;

  // Build effective fixed layer IDs - combine user toggles with auto-detection
  // In sticker mode, we return an empty set so no layers are treated as "fixed" background/frame
  const effectiveFixedLayerIds = useMemo(() => {
    // In sticker mode, no layers should be treated as fixed (background/frame)
    if (stickerMode) {
      return new Set<string>();
    }
    if (fixedLayerIds.size > 0) {
      return fixedLayerIds;
    }
    // Auto-detect based on naming
    const autoFixed = new Set<string>();
    layerAssets.forEach((layer) => {
      if (isFillLayerByDefault(layer)) {
        autoFixed.add(layer.id);
      }
    });
    return autoFixed;
  }, [layerAssets, fixedLayerIds, stickerMode]);

  // Handle image load - update bounds for min scale calculation
  const handleImageLoad = useCallback(
    (width: number, height: number) => {
      setImageBounds({ width, height });
    },
    [setImageBounds]
  );

  const handleExport = async () => {
    if (!imageUrl && !useLayerMode) return;

    setIsExporting(true);

    try {
      // Create high-res export canvas
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = EXPORT_SIZE;
      exportCanvas.height = EXPORT_SIZE;
      const ctx = exportCanvas.getContext('2d');
      if (!ctx) {
        throw new Error('Could not get canvas context');
      }

      const scaleFactor = EXPORT_SIZE / canvasSize;

      if (useLayerMode) {
        // Layer-based export
        await preloadLayerImages(layerAssets);

        for (const layer of layerAssets) {
          // In sticker mode, skip background and frame layers entirely
          if (stickerMode && isFillLayerByDefault(layer)) {
            continue;
          }

          const img = getDecodedImageFromCache(layer.uri);
          if (!img) continue;

          ctx.save();

          const isFixed = effectiveFixedLayerIds.has(layer.id);
          // Enable antialiasing for smooth resizing
          ctx.imageSmoothingEnabled = true;

          if (isFixed) {
            // Fill layers: cover the canvas
            const imageAspect = img.width / img.height;
            const canvasAspect = 1; // Square canvas

            let drawWidth: number;
            let drawHeight: number;

            if (imageAspect > canvasAspect) {
              drawHeight = EXPORT_SIZE;
              drawWidth = drawHeight * imageAspect;
            } else {
              drawWidth = EXPORT_SIZE;
              drawHeight = drawWidth / imageAspect;
            }

            const drawX = (EXPORT_SIZE - drawWidth) / 2;
            const drawY = (EXPORT_SIZE - drawHeight) / 2;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          } else {
            // Standard layers: transform with pan/zoom (dragging handles both X and Y)
            const drawWidth = EXPORT_SIZE * scale;
            const drawHeight = EXPORT_SIZE * scale;

            const drawX = (EXPORT_SIZE - drawWidth) / 2 + offset.x * scaleFactor;
            const drawY = (EXPORT_SIZE - drawHeight) / 2 + offset.y * scaleFactor;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
          }

          ctx.restore();
        }
      } else {
        // Legacy single image export
        const img = new Image();
        img.crossOrigin = 'anonymous';

        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            ctx.save();
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Calculate draw dimensions maintaining aspect ratio
            const imageAspect = img.width / img.height;
            let drawWidth: number;
            let drawHeight: number;

            if (imageAspect > 1) {
              drawHeight = EXPORT_SIZE * scale;
              drawWidth = drawHeight * imageAspect;
            } else {
              drawWidth = EXPORT_SIZE * scale;
              drawHeight = drawWidth / imageAspect;
            }

            const drawX = (EXPORT_SIZE - drawWidth) / 2 + offset.x * scaleFactor;
            const drawY = (EXPORT_SIZE - drawHeight) / 2 + offset.y * scaleFactor;

            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            ctx.restore();

            resolve();
          };
          img.onerror = () => reject(new Error('Failed to load image'));
        });

        img.src = imageUrl;
      }

      // Square crop - no clipping needed, export full canvas

      // Convert to PNG blob and download using smart download utility
      const blob = await new Promise<Blob | null>((resolve) => {
        exportCanvas.toBlob((b) => resolve(b), 'image/png');
      });

      if (blob) {
        const nftName = currentNFT?.name || 'avatar';
        const suffix = stickerMode ? '_sticker' : '_cropped';
        const filename = `${nftName.replace(/\s+/g, '_')}${suffix}.png`;

        const result = await downloadImage(blob, filename, {
          forceDownloadOnly: mode === 'export',
          onPreviewRequired: (dataUrl, fname) => {
            setPreviewImage({ url: dataUrl, filename: fname });
          },
          onTelegramSending: () => {
            setIsSending(true);
          },
          onTelegramSent: () => {
            setIsSending(false);
          },
        });

        // Only close modal if download succeeded and wasn't a preview fallback
        if (result.success && result.method !== 'preview') {
          // Show success notification based on method
          if (result.method === 'telegram') {
            // Use Telegram's native alert for best UX
            if (window.Telegram?.WebApp?.showAlert) {
              window.Telegram.WebApp.showAlert('Image sent to chat!', () => {
                closeModal();
              });
            } else {
              // Fallback to custom notification
              setSuccessMessage('Image sent to chat!');
              setTimeout(() => {
                setSuccessMessage(null);
                closeModal();
              }, 1500);
            }
          } else if (result.method === 'download') {
            setSuccessMessage('Image downloaded!');
            setTimeout(() => {
              setSuccessMessage(null);
              closeModal();
            }, 1500);
          } else if (result.method === 'share') {
            setSuccessMessage('Image shared!');
            setTimeout(() => {
              setSuccessMessage(null);
              closeModal();
            }, 1500);
          } else {
            closeModal();
          }
        }
      }
    } catch (error) {
      console.error('Failed to export avatar:', error);
      alert('Failed to export avatar. Please try again.');
    } finally {
      setIsExporting(false);
      setIsSending(false);
    }
  };

  const handleClose = () => {
    reset();
    closeModal();
  };

  const handleStickerModeChange = (checked: boolean) => {
    setStickerMode(checked);
    // Reset zoom/position when sticker mode is enabled
    if (checked) {
      reset();
    }
  };

  const handleZoomChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScale(parseFloat(e.target.value));
  };

  const zoomIn = () => {
    setScale(Math.min(3, scale + 0.1));
  };

  const zoomOut = () => {
    setScale(Math.max(minScale, scale - 0.1));
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Crop PFP"
      size="xl"
      noScroll
      headerActions={
        useLayerMode ? (
          <Toggle
            checked={stickerMode}
            onChange={handleStickerModeChange}
            label="Sticker"
          />
        ) : undefined
      }
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            variant="game3d"
            onClick={handleExport}
            disabled={(!imageUrl && !useLayerMode) || isExporting}
            isLoading={isExporting}
          >
            {mode === 'export' ? 'Export' : 'Send to Chat'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Canvas Preview */}
        <div className="flex flex-col items-center gap-2">
          <div
            ref={containerRef}
            className={`relative ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ touchAction: 'none' }}
          >
            <CropCanvas
              size={canvasSize}
              imageUrl={!useLayerMode ? imageUrl : undefined}
              layerAssets={useLayerMode ? layerAssets : undefined}
              offset={offset}
              scale={scale}
              showCircleMask={showCircleMask}
              onImageLoad={handleImageLoad}
              fixedLayerIds={useLayerMode ? effectiveFixedLayerIds : undefined}
              stickerMode={stickerMode}
            />
          </div>

          {!imageUrl && !useLayerMode && (
            <p className="text-sm text-text-muted dark:text-slate-400 italic">
              No NFT loaded. Search for an NFT to crop.
            </p>
          )}
        </div>

        {/* Zoom Controls - hidden in sticker mode */}
        {!stickerMode && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-text-dark dark:text-slate-200">Zoom Level</label>
              <span className="text-sm text-text-muted dark:text-slate-400">{Math.round(scale * 50)}</span>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                onClick={zoomOut}
                disabled={scale <= minScale}
                aria-label="Zoom Out"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                  />
                </svg>
              </Button>

              <input
                type="range"
                min={minScale}
                max="3"
                step="0.02"
                value={scale}
                onChange={handleZoomChange}
                className="flex-1 h-2 bg-surface-secondary dark:bg-slate-700 rounded-lg appearance-none cursor-pointer accent-game-blue"
              />

              <Button
                variant="secondary"
                size="sm"
                onClick={zoomIn}
                disabled={scale >= 3}
                aria-label="Zoom In"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                  />
                </svg>
              </Button>

              <Button variant="secondary" size="sm" onClick={reset} aria-label="Reset">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </Button>
            </div>

            <p className="text-xs text-text-muted dark:text-slate-400 text-center">
              Drag vertically to position character, scroll to zoom
            </p>
          </div>
        )}
      </div>

      {/* Image Preview Modal for Telegram fallback */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        onClose={() => {
          setPreviewImage(null);
          closeModal();
        }}
        imageUrl={previewImage?.url || ''}
        filename={previewImage?.filename || ''}
      />

      {/* Sending Modal */}
      {isSending && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 mx-4 max-w-sm w-full text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-game-blue border-t-transparent rounded-full animate-spin" />
              <div>
                <h3 className="text-lg font-semibold text-text-dark dark:text-slate-100">
                  {mode === 'export' ? 'Exporting' : 'Sending to Chat'}
                </h3>
                <p className="text-sm text-text-muted dark:text-slate-400 mt-1">
                  {mode === 'export' ? 'Your image is being exported...' : 'Your image is being sent...'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification Modal */}
      {successMessage && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 mx-4 max-w-sm w-full text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-text-dark dark:text-slate-100">
                  {successMessage}
                </h3>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
